import React from 'react';
import PropTypes from 'prop-types';
import {
  Menu,
  MenuList,
  MenuButton,
  MenuPopover,
  MenuItems,
  MenuItem,
  MenuLink
} from '@reach/menu-button';
import ActionsArrowDown from '@strava/icons/ActionsArrowDownNormalXsmall';

import './_reach.scss';
import styles from './styles.scss';
import buttonStyles from '../Button/styles.scss';
import cx from '../clsx';

//
// Menu
//
// Reach UI docs: https://reach.tech/menu-button/#menu
// --------------------

export function DropdownMenu({ children, ...otherProps }) {
  return <Menu {...otherProps}>{children}</Menu>;
}

// Override Menu display name so that error from prop types show 'DropdownMenu'
Menu.displayName = 'DropdownMenu';

DropdownMenu.defaultProps = {
  children: null
};

DropdownMenu.propTypes = {
  children: PropTypes.node
};

//
// MenuButton
//
// Reach UI docs: https://reach.tech/menu-button/#menubutton
// --------------------

export function DropdownButton({
  className,
  children,
  onClick,
  ...otherProps
}) {
  return (
    <MenuButton
      className={cx(
        buttonStyles.btn,
        buttonStyles.default,
        styles.btn,
        className
      )}
      onClick={onClick}
      {...otherProps}
    >
      {children}
    </MenuButton>
  );
}

// Override MenuButton display name so that error from prop types show 'DropdownButton'
MenuButton.displayName = 'DropdownButton';

DropdownButton.defaultProps = {
  className: null,
  children: null,
  onClick: () => {}
};

DropdownButton.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  onClick: PropTypes.func
};

//
// MenuList
//
// Reach UI docs: https://reach.tech/menu-button/#menulist
// --------------------

export function DropdownList({ className, children, ...otherProps }) {
  return (
    <MenuList className={cx(styles.list, className)} {...otherProps}>
      {children}
    </MenuList>
  );
}

// Override Menu display name so that error from prop types show 'DropdownList'
MenuList.displayName = 'DropdownList';

DropdownList.defaultProps = {
  children: null,
  className: null
};

DropdownList.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
};

//
// MenuPopover
//
// Reach UI docs: https://reach.tech/menu-button/#menupopover
// --------------------

export function DropdownPopover({
  className,
  children,
  portal,
  ...otherProps
}) {
  return (
    <MenuPopover
      className={cx(styles.list, className)}
      portal={portal}
      {...otherProps}
    >
      {children}
    </MenuPopover>
  );
}

// Override Menu display name so that error from prop types show 'DropdownPopover'
Menu.displayName = 'DropdownPopover';

DropdownPopover.defaultProps = {
  children: null,
  className: null,
  portal: false
};

DropdownPopover.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  portal: PropTypes.bool
};

//
// MenuItems
//
// Reach UI docs: https://reach.tech/menu-button/#menuitems
// --------------------

export function DropdownItems({ className, children, ...otherProps }) {
  return (
    <MenuItems className={cx(styles.list, className)} {...otherProps}>
      {children}
    </MenuItems>
  );
}

// Override Menu display name so that error from prop types show 'DropdownItems'
MenuItems.displayName = 'DropdownItems';

DropdownItems.defaultProps = {
  children: null,
  className: null
};

DropdownItems.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
};

//
// DropdownLink
//
// Reach UI docs: https://reach.tech/menu-button/#menulink
// --------------------

export function DropdownLink({ as, href, children, className, ...otherProps }) {
  return (
    <MenuLink
      as={as}
      className={cx(styles.item, className)}
      href={href}
      {...otherProps}
    >
      {children}
    </MenuLink>
  );
}

// Override Menu display name so that error from prop types show 'DropdownLink'
MenuLink.displayName = 'DropdownLink';

DropdownLink.defaultProps = {
  as: 'a',
  className: null
};

DropdownLink.propTypes = {
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
  className: PropTypes.string,
  href: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]).isRequired,
  children: PropTypes.node.isRequired
};

//
// DropdownItem
//
// Reach UI docs: https://reach.tech/menu-button/#menuitem
// --------------------

export function DropdownItem({
  as,
  onSelect,
  children,
  className,
  ...otherProps
}) {
  return (
    <MenuItem
      as={as}
      className={cx(styles.item, className)}
      onSelect={onSelect}
      {...otherProps}
    >
      {children}
    </MenuItem>
  );
}

// Override Menu display name so that error from prop types show 'DropdownItem'
MenuItem.displayName = 'DropdownItem';

DropdownItem.propTypes = {
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
  className: PropTypes.string,
  onSelect: PropTypes.func,
  children: PropTypes.node.isRequired
};

DropdownItem.defaultProps = {
  as: 'div',
  className: null,
  onSelect: () => {}
};

//
// Dropdown
// --------------------

export function Dropdown({ text, children, onClick, className }) {
  return (
    <DropdownMenu>
      <DropdownButton className={className} onClick={onClick}>
        {text}
        <ActionsArrowDown className={styles.icon} />
      </DropdownButton>
      <DropdownList className={className}>{children}</DropdownList>
    </DropdownMenu>
  );
}

Dropdown.defaultProps = {
  className: null,
  onClick: () => {}
};

Dropdown.propTypes = {
  text: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  onClick: PropTypes.func
};
